import React, { Component } from "react";
import moment from "moment";
import * as Cookies from "es-cookie";
import { Row, Col, Modal } from "react-bootstrap";
import { components } from "react-select";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TabSection from "./TabSection";
import SelectDropDown from "../selectdropdown/SelectDropDown";
import ProfileDateBlock from "./ProfileDateBlock";
import { PageShimmer } from "./Shimmer";
import { pointTooltip } from "./chartTooltip";

// College bridge: the viewed student comes from props (route state). Different
// entry points name the mobile field differently, so check every known variant.
// The student-app tab keys on mobile.
const resolveMobile = (student) => {
  if (!student) return null;
  return (
    student.contact_no || student.student_mobile || student.mobile ||
    student.studentMobile || student.parent_mobile || student.phone ||
    student.mobile_no || student.username || null
  );
};

// College view: this profile shows ANOTHER student, so "You/Your" becomes the
// student's (first) name. Falls back to "Student" if no name.
const studentName = (student) => {
  const full = (student && (student.student_name || student.name)) || "";
  return full.trim() ? full.trim() : "Student";
};
const studentFirstName = (student) => studentName(student).split(/\s+/)[0];

const DropdownIndicator = (props) =>
  components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      <i className="fal fa-angle-down" />
    </components.DropdownIndicator>
  );

const examTypes = [
  { value: 0, label: "All Exams" },
  { value: 1, label: "Mock Test" },
  { value: 2, label: "Custom Exam" },
  { value: 3, label: "Scheduled Exam" },
  { value: 4, label: "Error Exam" },
  { value: 6, label: "Practice Test" },
  { value: 7, label: "Adaptive Test" },
];

const GET_STUDENT_PERFORMANCE_REPORT = gql`
  query (
    $mobile: String
    $student_user_id: Int
    $edu_student_id: Int
    $exam_type: Int
    $exam_stream: Int
    $exam_id: Int
    $fromDate: String
    $toDate: String
  ) {
    getStudentPerformanceReport(
      mobile: $mobile
      student_user_id: $student_user_id
      edu_student_id: $edu_student_id
      exam_type: $exam_type
      exam_stream: $exam_stream
      fromDate: $fromDate
      toDate: $toDate
    ) {
      subjectPerformance {
        subject_id
        subject
        best_score
        lowest_score
        section_avg
        branch_avg
      }
      chapterPerformance(exampaper_id: $exam_id) {
        subject_id
        subject
        chapter_id
        chapter
        performance
        performance_count
        accuracy_percentage
      }
      topicPerformance(exampaper_id: $exam_id) {
        subject_id
        subject
        chapter_id
        chapter
        topic_id
        topic
        attempted
        performance
        accuracy_percentage
      }
      accuracyAnalysis(exampaper_id: $exam_id) {
        difficulty
        correct
        wrong
        not_answered
        attempted
        correct_percentage
        wrong_percentage
      }
      examWiseResult {
        id
        exam_name
      }
      examSubjectMatrix {
        exam_paper_id
        session_id
        exam_name
        date
        total_marks
        total_questions
        subjects {
          subject_id
          subject
          questions
          subject_max
          attempted
          accuracy
          score
        }
      }
    }
  }
`;

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Parse an exam date (handles common day/month/ISO orderings) into a sortable number.
const parseExamDate = (d) => {
  if (!d) return 0;
  const m = moment(
    d,
    ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601],
    true,
  );
  return m.isValid() ? m.valueOf() : 0;
};

// Pick the exam paper with the latest date (falls back to the last array item).
const pickLatestExam = (matrix = []) => {
  if (!matrix || !matrix.length) return null;
  return matrix.reduce(
    (best, e) => (parseExamDate(e.date) >= parseExamDate(best.date) ? e : best),
    matrix[0],
  );
};

class SubjectsTopicsTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedExamValue: { value: 0, label: "All Exams" },
      startDateCalender: moment(new Date())
        .subtract(1, "month")
        .startOf("day")
        .unix(),
      endDateCalender: moment(new Date()).endOf("day").unix(),
      overallData: [],
      subjectPerformance: [],
      chapterPerformance: [],
      topicPerformance: [], // per-topic accuracy (granular drill-down)
      accuracyAnalysis: [], // difficulty-wise correct/wrong/not_answered
      panelView: "graph", // "graph" | "table" — shared toggle for both top panels
      subjectView: "graph", // "graph" | "table"
      accuracyView: "graph", // "graph" | "table"
      chapterView: "heatmap", // "heatmap" | "table" — heatmap shown first
      chapterModal: null, // { subject, chapter_id, chapter } whose topics are open
      examSubjectMatrix: [], // per-exam × per-subject attempted/accuracy/score (all exams)
      examPapers: [], // { value: exam_paper_id, label: exam_name } from examWiseResult
      selectedExamPaper: null, // defaults to the last exam
      loading: false,
    };
  }

  componentDidMount() {
    const { selectedExamValue, startDateCalender, endDateCalender } =
      this.state;
    this.getReport(
      selectedExamValue?.value,
      startDateCalender,
      endDateCalender,
    );
  }

  // Re-fetch when the header exam-stream (Mains/Advanced) changes.
  componentDidUpdate(prevProps) {
    if (prevProps.examStream !== this.props.examStream) {
      const { selectedExamValue, startDateCalender, endDateCalender } =
        this.state;
      this.getReport(
        selectedExamValue?.value,
        startDateCalender,
        endDateCalender,
      );
    }
  }

  selecthandleInputChange = (name, value, selectedOption) => {
    this.setState({ [name]: selectedOption }, () => {
      this.getReport(
        selectedOption?.value,
        this.state.startDateCalender,
        this.state.endDateCalender,
      );
    });
  };

  // Exam-paper dropdown — accuracyAnalysis is per exam, so refetch with its exam_id.
  // (The subject matrix already holds every exam, so its chart switches instantly too.)
  handleExamPaperChange = (name, value, selectedOption) => {
    this.setState({ selectedExamPaper: selectedOption }, () => {
      this.getReport(
        this.state.selectedExamValue?.value,
        this.state.startDateCalender,
        this.state.endDateCalender,
        selectedOption?.value,
      );
    });
  };

  applyCallback = (startDate, endDate) => {
    const start = startDate.clone().startOf("day").unix();
    const end = endDate.clone().endOf("day").unix();
    this.setState({ startDateCalender: start, endDateCalender: end }, () => {
      this.getReport(this.state.selectedExamValue?.value, start, end);
    });
  };

  getReport = async (examType, fromDate, toDate, examId) => {
    const fromDateFinal = fromDate || this.state.startDateCalender;
    const toDateFinal = toDate || this.state.endDateCalender;
    const examTypeFinal = examType ?? this.state.selectedExamValue?.value;
    // Explicit examId wins; otherwise keep the current exam-paper selection.
    // exam_id 0 = "All exams (cumulative)" → pass null so no single-paper filter.
    const rawExamId =
      examId != null ? examId : (this.state.selectedExamPaper?.value ?? null);
    const examIdFinal = rawExamId ? rawExamId : null;

    this.setState({ loading: true });
    try {
      const result = await this.props.client.query({
        query: GET_STUDENT_PERFORMANCE_REPORT,
        variables: {
          mobile: `${resolveMobile(this.props.student) || ""}`,
          // Backstop: college identity so the backend resolves the student even
          // when the passed student object has no mobile field.
          student_user_id: parseInt(this.props.student?.student_user_id) || 0,
          edu_student_id: parseInt(this.props.student?.edu_student_id) || 0,
          exam_type: examTypeFinal,
          exam_stream:
            this.props.examStream != null
              ? Number(this.props.examStream)
              : null,
          exam_id: examIdFinal,
          fromDate: fromDateFinal.toString(),
          toDate: toDateFinal.toString(),
        },
        fetchPolicy: "network-only",
      });
      const data = result?.data?.getStudentPerformanceReport;
      const matrix = data?.examSubjectMatrix || [];
      // Build the dropdown from the matrix so its value (exam_paper_id) matches the chart lookup.
      // An "All exams" option (value 0) gives the cumulative chapter/topic view.
      const papers = [
        { value: 0, label: "All exams (cumulative)" },
        ...matrix.map((m) => ({ value: m.exam_paper_id, label: m.exam_name })),
      ];
      const update = {
        overallData: data?.overall || [],
        subjectPerformance: data?.subjectPerformance || [],
        chapterPerformance: data?.chapterPerformance || [],
        topicPerformance: data?.topicPerformance || [],
        accuracyAnalysis: data?.accuracyAnalysis || [],
        examSubjectMatrix: matrix,
        examPapers: papers,
        loading: false,
      };
      // First load with no exam selected → default to the LATEST exam (by date) and refetch its accuracyAnalysis.
      if (
        this.state.selectedExamPaper == null &&
        examIdFinal == null &&
        papers.length
      ) {
        const latestExam = pickLatestExam(matrix);
        const latest = latestExam
          ? { value: latestExam.exam_paper_id, label: latestExam.exam_name }
          : papers[papers.length - 1];
        update.selectedExamPaper = latest;
        this.setState(update, () =>
          this.getReport(
            examTypeFinal,
            fromDateFinal,
            toDateFinal,
            latest.value,
          ),
        );
        return;
      }
      this.setState(update);
    } catch (error) {
      console.log(error);
      this.setState({
        overallData: [],
        subjectPerformance: [],
        chapterPerformance: [],
        accuracyAnalysis: [],
        examSubjectMatrix: [],
        loading: false,
      });
    }
  };

  // Compact Exam + Date controls rendered in the TabSection header (right side).
  renderControls = () => (
    <div
      className="d-flex flex-wrap align-items-center justify-content-end"
      style={{ gap: "12px" }}
    >
      <div className="d-flex align-items-center" style={{ gap: "8px" }}>
        <span
          className="font-weight-bold text-muted"
          style={{ fontSize: "13px" }}
        >
          Exam
        </span>
        <div style={{ minWidth: "170px" }}>
          <SelectDropDown
            stateData={this.state.selectedExamValue}
            handleChange={this.selecthandleInputChange}
            name="selectedExamValue"
            options={examTypes}
            placeholderName="Exam"
            dropdownIndicator={{ DropdownIndicator }}
          />
        </div>
      </div>
      {this.state.examPapers.length > 0 && (
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <span
            className="font-weight-bold text-muted"
            style={{ fontSize: "13px" }}
          >
            Exam Paper
          </span>
          <div style={{ minWidth: "210px" }}>
            <SelectDropDown
              stateData={this.state.selectedExamPaper}
              handleChange={this.handleExamPaperChange}
              name="selectedExamPaper"
              options={this.state.examPapers}
              placeholderName="Exam Paper"
              dropdownIndicator={{ DropdownIndicator }}
            />
          </div>
        </div>
      )}
      <div className="d-flex align-items-center" style={{ gap: "8px" }}>
        <span
          className="font-weight-bold text-muted"
          style={{ fontSize: "13px" }}
        >
          Date
        </span>
        <ProfileDateBlock
          applyCallback={this.applyCallback}
          start={moment.unix(this.state.startDateCalender)}
          end={moment.unix(this.state.endDateCalender)}
        />
      </div>
    </div>
  );

  // Subject-wise Performance — best/lowest score vs branch & section averages per subject.
  renderSubjectWisePerformance = (subjectPerformance = []) => {
    const options = {
      chart: {
        type: "bar",
        backgroundColor: "transparent",
        borderRadius: 10,
        spacingLeft: 20,
        height: 320,
        scrollablePlotArea: {
          minWidth:
            subjectPerformance?.length > 10
              ? subjectPerformance.length * 80
              : null,
          scrollPositionX: 0,
        },
      },
      title: { text: "" },
      credits: { enabled: false },
      xAxis: {
        categories: subjectPerformance.map((item) => item.subject),
        scrollbar: { enabled: true },
        labels: { style: { fontWeight: "600", color: "#000" } },
      },
      yAxis: {
        title: { text: "Score" },
        tickInterval: 5,
      },
      tooltip: pointTooltip({ unitBySeries: { "Accuracy %": "%" } }),
      plotOptions: {
        bar: { dataLabels: { enabled: true } },
        series: { borderRadius: 6, pointPadding: 0.1, groupPadding: 0.2 },
      },
      legend: {
        layout: "vertical",
        align: "right",
        verticalAlign: "top",
        x: -40,
        y: 80,
      },
      series: [
        {
          name: "Best Score",
          data: subjectPerformance.map((i) => num(i.best_score)),
          color: "#1f4e79",
        },
        {
          name: "Lowest Score",
          data: subjectPerformance.map((i) => num(i.lowest_score)),
          color: "#2ecc71",
        },
        {
          name: "Branch Avg",
          data: subjectPerformance.map((i) => num(i.branch_avg)),
          color: "#007bff",
        },
        {
          name: "Section Avg",
          data: subjectPerformance.map((i) => num(i.section_avg)),
          color: "#e74c3c",
        },
      ],
      exporting: { enabled: true },
    };

    const view = this.state.subjectView;
    return (
      <div
        className="p-3 h-100"
        style={{
          borderRadius: "16px",
          border: "1px solid #f1f2f5",
          background: "#ffffff",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between flex-wrap mb-3"
          style={{ gap: 8 }}
        >
          <h6
            className="font-weight-bold text-dark mb-0"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Overall Subject Wise Performance
          </h6>
          <div className="sp-segment">
            <button
              type="button"
              className={view === "table" ? "active" : ""}
              onClick={() => this.setState({ subjectView: "table" })}
            >
              <i className="fas fa-table" />
              Table
            </button>
            <button
              type="button"
              className={view === "graph" ? "active" : ""}
              onClick={() => this.setState({ subjectView: "graph" })}
            >
              <i className="fas fa-chart-bar" />
              Graph
            </button>
          </div>
        </div>
        {subjectPerformance.length > 0 ? (
          view === "graph" ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
              containerProps={{ style: { width: "100%" } }}
            />
          ) : (
            <div className="table-responsive">
              <table
                className="table table-borderless mb-0"
                style={{ fontSize: 13 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                    <th
                      className="text-muted"
                      style={{ fontSize: 11, textTransform: "uppercase" }}
                    >
                      Subject
                    </th>
                    <th
                      className="text-muted text-center"
                      style={{ fontSize: 11, textTransform: "uppercase" }}
                    >
                      Best
                    </th>
                    <th
                      className="text-muted text-center"
                      style={{ fontSize: 11, textTransform: "uppercase" }}
                    >
                      Lowest
                    </th>
                    <th
                      className="text-muted text-center"
                      style={{ fontSize: 11, textTransform: "uppercase" }}
                    >
                      Branch Avg
                    </th>
                    <th
                      className="text-muted text-center"
                      style={{ fontSize: 11, textTransform: "uppercase" }}
                    >
                      Section Avg
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {subjectPerformance.map((r) => (
                    <tr
                      key={r.subject_id ?? r.subject}
                      style={{ borderBottom: "1px solid #f6f8fb" }}
                    >
                      <td className="font-weight-bold text-dark">
                        {r.subject || "-"}
                      </td>
                      <td className="text-center">{num(r.best_score)}</td>
                      <td className="text-center">{num(r.lowest_score)}</td>
                      <td className="text-center">{num(r.branch_avg)}</td>
                      <td className="text-center">{num(r.section_avg)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div
            className="text-center text-muted py-4"
            style={{ fontSize: "14px" }}
          >
            No subject-wise performance available.
          </div>
        )}
      </div>
    );
  };

  // Accuracy Analysis — difficulty-wise correct/wrong/not-answered, table & graph.
  renderAccuracyAnalysis = (rows = []) => {
    const view = this.state.panelView;
    const categories = rows.map((r) => r.difficulty);
    // Questions-axis max = the largest total (correct + wrong + not-answered) among
    // the difficulty bands, so each stack reads as "out of the questions in that
    // band". Dynamic per exam; falls back to auto when unknown.
    const bandTotalMax = rows.reduce(
      (mx, r) => Math.max(mx, num(r.correct) + num(r.wrong) + num(r.not_answered)),
      0,
    );
    // Combo chart: columns = Correct/Wrong/Not-Answered counts (left axis),
    // line = Correct % on a second axis (right).
    const options = {
      chart: { backgroundColor: "transparent", height: 320 },
      title: { text: "" },
      credits: { enabled: false },
      xAxis: {
        categories,
        crosshair: true,
        lineColor: "#e2e8f0",
        labels: { style: { color: "#64748b", fontWeight: "600" } },
      },
      yAxis: [
        {
          min: 0,
          max: bandTotalMax > 0 ? bandTotalMax : null,
          // Keep the axis exactly at the band max, but still show a label there
          // (endOnTick alone hides the top label, making 145 look like 100).
          endOnTick: false,
          startOnTick: true,
          maxPadding: 0,
          showLastLabel: true,
          tickPositioner: bandTotalMax > 0
            ? function () {
                // Evenly spaced ticks that always include 0 and the real max.
                const step = Math.ceil(bandTotalMax / 4);
                const ticks = [];
                for (let v = 0; v < bandTotalMax; v += step) ticks.push(v);
                ticks.push(bandTotalMax);
                return ticks;
              }
            : undefined,
          title: {
            text: bandTotalMax > 0 ? `Questions (max ${bandTotalMax})` : "Questions",
            style: { color: "#94a3b8" },
          },
          gridLineColor: "#eef2f6",
          labels: { style: { color: "#64748b" } },
        },
        {
          min: 0,
          max: 100,
          endOnTick: false,
          maxPadding: 0,
          opposite: true,
          gridLineWidth: 0,
          title: { text: "Correct %", style: { color: "#94a3b8" } },
          labels: { format: "{value}%", style: { color: "#6366f1" } },
        },
      ],
      tooltip: pointTooltip({ unitBySeries: { "Correct %": "%" } }),
      legend: { align: "center", verticalAlign: "top" },
      plotOptions: {
        column: {
          borderRadius: 4,
          groupPadding: 0.12,
          dataLabels: {
            enabled: true,
            style: {
              fontWeight: "700",
              fontSize: "10.5px",
              textOutline: "none",
            },
          },
        },
        spline: {
          dataLabels: {
            enabled: true,
            style: { fontWeight: "700", color: "#334155" },
          },
        },
      },
      series: [
        {
          name: "Correct",
          type: "column",
          yAxis: 0,
          data: rows.map((r) => num(r.correct)),
          color: "#22c55e",
        },
        {
          name: "Wrong",
          type: "column",
          yAxis: 0,
          data: rows.map((r) => num(r.wrong)),
          color: "#ef4444",
        },
        {
          name: "Not Answered",
          type: "column",
          yAxis: 0,
          data: rows.map((r) => num(r.not_answered)),
          color: "#94a3b8",
        },
        {
          name: "Correct %",
          type: "spline",
          yAxis: 1,
          data: rows.map((r) => num(r.correct_percentage)),
          color: "#6366f1",
          lineWidth: 3,
          marker: { enabled: true, radius: 5 },
          tooltip: { valueSuffix: "%" },
          zIndex: 5,
        },
      ],
    };
    return (
      <div
        className="p-3 h-100"
        style={{
          borderRadius: "16px",
          border: "1px solid #f1f2f5",
          background: "#ffffff",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between flex-wrap mb-3"
          style={{ gap: 8 }}
        >
          <h6
            className="font-weight-bold text-dark mb-0"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Accuracy Analysis
          </h6>
        </div>
        {rows.length > 0 ? (
          <>
            {view === "graph" ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { width: "100%" } }}
              />
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-borderless mb-0"
                  style={{ fontSize: 12.5 }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                      <th
                        className="text-muted"
                        style={{ fontSize: 11, textTransform: "uppercase" }}
                      >
                        Difficulty
                      </th>
                      <th
                        className="text-muted text-center"
                        style={{ fontSize: 11, textTransform: "uppercase" }}
                      >
                        Correct
                      </th>
                      <th
                        className="text-muted text-center"
                        style={{ fontSize: 11, textTransform: "uppercase" }}
                      >
                        Wrong
                      </th>
                      <th
                        className="text-muted text-center"
                        style={{ fontSize: 11, textTransform: "uppercase" }}
                      >
                        Not Ans
                      </th>
                      <th
                        className="text-muted text-center"
                        style={{ fontSize: 11, textTransform: "uppercase" }}
                      >
                        Attempted
                      </th>
                      <th
                        className="text-muted text-center"
                        style={{ fontSize: 11, textTransform: "uppercase" }}
                      >
                        Correct %
                      </th>
                      <th
                        className="text-muted text-center"
                        style={{ fontSize: 11, textTransform: "uppercase" }}
                      >
                        Wrong %
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f6f8fb" }}>
                        <td className="font-weight-bold text-dark">
                          {r.difficulty || "-"}
                        </td>
                        <td
                          className="text-center"
                          style={{ color: "#16a34a", fontWeight: 600 }}
                        >
                          {num(r.correct)}
                        </td>
                        <td
                          className="text-center"
                          style={{ color: "#dc2626", fontWeight: 600 }}
                        >
                          {num(r.wrong)}
                        </td>
                        <td className="text-center text-muted">
                          {num(r.not_answered)}
                        </td>
                        <td className="text-center">{num(r.attempted)}</td>
                        <td
                          className="text-center"
                          style={{ color: "#16a34a", fontWeight: 600 }}
                        >
                          {num(r.correct_percentage)}%
                        </td>
                        <td
                          className="text-center"
                          style={{ color: "#dc2626", fontWeight: 600 }}
                        >
                          {num(r.wrong_percentage)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Row className="mt-3">
              {rows.map((r, i) => (
                <Col md={4} sm={6} key={r.difficulty ?? i} className="mb-3">
                  <div
                    className="p-3 h-100"
                    style={{
                      borderRadius: 14,
                      border: "1px solid #f1f2f5",
                      background: "#fafaff",
                    }}
                  >
                    <div
                      className="font-weight-bold text-dark mb-2"
                      style={{ fontSize: 14 }}
                    >
                      {r.difficulty || "-"}
                    </div>
                    <div
                      className="d-flex justify-content-between text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      <span>Correct</span>
                      <span
                        className="font-weight-bold"
                        style={{ color: "#16a34a" }}
                      >
                        {num(r.correct)}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      <span>Wrong</span>
                      <span
                        className="font-weight-bold"
                        style={{ color: "#dc2626" }}
                      >
                        {num(r.wrong)}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      <span>Not Answered</span>
                      <span className="font-weight-bold text-dark">
                        {num(r.not_answered)}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      <span>Accuracy</span>
                      <span
                        className="font-weight-bold"
                        style={{
                          color: this.accuracyColor(num(r.correct_percentage)),
                        }}
                      >
                        {num(r.correct_percentage)}%
                      </span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div
            className="text-center text-muted py-4"
            style={{ fontSize: "14px" }}
          >
            No accuracy analysis available.
          </div>
        )}
      </div>
    );
  };

  // Accuracy color — red = weak, amber = average, green = strong.
  accuracyColor = (pct) => {
    if (pct < 60) return "#ef4444"; // red = weak
    if (pct < 75) return "#f59e0b"; // amber = average
    return "#16a34a"; // green = strong
  };

  // Single chapter row: name on the left, % on the right, progress bar below.
  renderChapterRow = (chapter) => {
    const pct = num(chapter.accuracy_percentage);
    const color = this.accuracyColor(pct);
    return (
      <div key={chapter.chapter_id ?? chapter.chapter} className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <span className="text-dark" style={{ fontSize: "14px" }}>
            {chapter.chapter || "-"}
          </span>
          <span
            className="font-weight-bold"
            style={{ fontSize: "14px", color }}
          >
            {pct}%
          </span>
        </div>
        <div
          style={{
            height: "8px",
            borderRadius: "6px",
            background: "#eef2f6",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.min(Math.max(pct, 0), 100)}%`,
              height: "100%",
              borderRadius: "6px",
              background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`,
            }}
          />
        </div>
      </div>
    );
  };

  // Chapter / Topic-wise Accuracy — chapters grouped by subject, two per row.
  // Soft background tint for a heatmap cell based on accuracy.
  accuracyCellBg = (pct) => {
    if (pct < 60) return "#fef2f2"; // red-50
    if (pct < 75) return "#fffbeb"; // amber-50
    return "#ecfdf5"; // green-50
  };

  // Count of topics available for a given chapter (drives the click affordance).
  topicsForChapter = (subject_id, chapter_id) =>
    (this.state.topicPerformance || []).filter(
      (t) =>
        String(t.subject_id) === String(subject_id) &&
        String(t.chapter_id) === String(chapter_id),
    );

  openChapterTopics = (c) => {
    const topics = this.topicsForChapter(c.subject_id, c.chapter_id);
    if (!topics.length) return; // nothing to drill into
    this.setState({
      chapterModal: {
        subject: c.subject,
        chapter: c.chapter,
        chapter_id: c.chapter_id,
        accuracy: num(c.accuracy_percentage),
        topics,
      },
    });
  };
  closeChapterTopics = () => this.setState({ chapterModal: null });

  // Chapter & Topic accuracy — the student's OWN weak areas. Heatmap first
  // (chapters colored by accuracy, click a chapter to see its topics), with a
  // table toggle second. Requirement: "chapter-wise & topic-wise accuracy —
  // exact weak areas at a granular level."
  renderChapterWiseAccuracy = (chapterPerformance = []) => {
    // Group chapters under their subject, preserving first-seen order.
    const groups = [];
    const bySubject = {};
    chapterPerformance.forEach((c) => {
      const key = c.subject_id ?? c.subject;
      if (!bySubject[key]) {
        bySubject[key] = {
          subject: c.subject,
          subject_id: c.subject_id,
          chapters: [],
        };
        groups.push(bySubject[key]);
      }
      bySubject[key].chapters.push(c);
    });

    // Per-subject accuracy summary (attempt-agnostic mean of its chapters).
    const subjectSummary = groups.map((g) => {
      const vals = g.chapters
        .map((c) => num(c.accuracy_percentage))
        .filter((v) => v > 0);
      const avg = vals.length
        ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
        : 0;
      return {
        subject: g.subject,
        avg,
        weak: g.chapters.filter((c) => num(c.accuracy_percentage) < 60).length,
      };
    });

    const view = this.state.chapterView;

    return (
      <div
        className="p-3 mt-4"
        style={{
          borderRadius: "16px",
          border: "1px solid #f1f2f5",
          background: "#ffffff",
        }}
      >
        <div
          className="d-flex align-items-start justify-content-between flex-wrap mb-3"
          style={{ gap: 10 }}
        >
          <div>
            <h6
              className="font-weight-bold text-dark mb-1"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Chapter &amp; Topic Accuracy
            </h6>
            <span className="text-muted" style={{ fontSize: "11.5px" }}>
              Click a chapter to see its topics ·{" "}
              <span style={{ color: "#16a34a" }}>green</span> strong ·{" "}
              <span style={{ color: "#f59e0b" }}>amber</span> average ·{" "}
              <span style={{ color: "#ef4444" }}>red</span> weak
            </span>
          </div>
          {/* Heatmap first, table second. */}
          <div className="sp-segment sp-segment-sm">
            <button
              type="button"
              className={view === "heatmap" ? "active" : ""}
              onClick={() => this.setState({ chapterView: "heatmap" })}
            >
              <i className="fas fa-th" />
              Heatmap
            </button>
            <button
              type="button"
              className={view === "table" ? "active" : ""}
              onClick={() => this.setState({ chapterView: "table" })}
            >
              <i className="fas fa-table" />
              Table
            </button>
          </div>
        </div>

        {/* Subject accuracy summary chips. */}
        {subjectSummary.length > 0 && (
          <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
            {subjectSummary.map((s) => (
              <div
                key={s.subject}
                className="d-flex align-items-center px-3 py-2"
                style={{
                  gap: 8,
                  borderRadius: 12,
                  background: "#fafaff",
                  border: "1px solid #f1f2f5",
                }}
              >
                <span
                  className="font-weight-bold text-dark"
                  style={{ fontSize: 13 }}
                >
                  {s.subject || "-"}
                </span>
                <span
                  className="font-weight-bold"
                  style={{ fontSize: 15, color: this.accuracyColor(s.avg) }}
                >
                  {s.avg}%
                </span>
                {s.weak > 0 ? (
                  <span
                    style={{ fontSize: 11, color: "#ef4444", fontWeight: 700 }}
                  >
                    {s.weak} weak
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {groups.length === 0 ? (
          <div
            className="text-center text-muted py-4"
            style={{ fontSize: "14px" }}
          >
            No chapter-wise accuracy available.
          </div>
        ) : view === "heatmap" ? (
          <Row>
            {groups.map((g) => (
              <Col md={6} key={g.subject} className="mb-4">
                <h6
                  className="font-weight-bold text-dark mb-2"
                  style={{ fontSize: "14px" }}
                >
                  {g.subject || "-"}
                </h6>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {g.chapters.map((c) => {
                    const pct = num(c.accuracy_percentage);
                    const color = this.accuracyColor(pct);
                    const topics = this.topicsForChapter(
                      c.subject_id,
                      c.chapter_id,
                    );
                    const clickable = topics.length > 0;
                    return (
                      <div
                        key={c.chapter_id ?? c.chapter}
                        onClick={
                          clickable
                            ? () => this.openChapterTopics(c)
                            : undefined
                        }
                        title={
                          clickable
                            ? `${topics.length} topics — click to view`
                            : "No topic breakdown"
                        }
                        className="sp-heat-cell"
                        style={{
                          cursor: clickable ? "pointer" : "default",
                          background: this.accuracyCellBg(pct),
                          borderLeft: `3px solid ${color}`,
                          borderRadius: 10,
                          padding: "10px 12px",
                          minWidth: 140,
                          flex: "1 1 140px",
                        }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-between"
                          style={{ gap: 8 }}
                        >
                          <span
                            className="text-dark"
                            style={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            {c.chapter || "-"}
                          </span>
                          <span
                            className="font-weight-bold"
                            style={{ fontSize: 14, color }}
                          >
                            {pct}%
                          </span>
                        </div>
                        {clickable ? (
                          <span
                            className="text-muted"
                            style={{ fontSize: 10.5 }}
                          >
                            <i className="fas fa-layer-group mr-1" />
                            {topics.length} topics
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="table-responsive">
            <table className="table table-borderless mb-0">
              <thead>
                <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                  <th
                    className="text-muted"
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Subject
                  </th>
                  <th
                    className="text-muted"
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Chapter
                  </th>
                  <th
                    className="text-muted text-right"
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Accuracy
                  </th>
                  <th
                    className="text-muted text-right"
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Topics
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.flatMap((g) =>
                  g.chapters.map((c) => {
                    const pct = num(c.accuracy_percentage);
                    const topics = this.topicsForChapter(
                      c.subject_id,
                      c.chapter_id,
                    );
                    return (
                      <tr
                        key={`${g.subject}-${c.chapter_id}`}
                        style={{
                          borderBottom: "1px solid #f6f8fb",
                          cursor: topics.length ? "pointer" : "default",
                        }}
                        onClick={
                          topics.length
                            ? () => this.openChapterTopics(c)
                            : undefined
                        }
                      >
                        <td className="text-muted" style={{ fontSize: 13 }}>
                          {g.subject || "-"}
                        </td>
                        <td
                          className="font-weight-bold text-dark"
                          style={{ fontSize: 13 }}
                        >
                          {c.chapter || "-"}
                        </td>
                        <td
                          className="text-right font-weight-bold"
                          style={{
                            fontSize: 13,
                            color: this.accuracyColor(pct),
                          }}
                        >
                          {pct}%
                        </td>
                        <td
                          className="text-right text-muted"
                          style={{ fontSize: 12.5 }}
                        >
                          {topics.length ? (
                            <>
                              <i className="fas fa-layer-group mr-1" />
                              {topics.length}
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table>
          </div>
        )}

        {this.renderTopicModal()}
      </div>
    );
  };

  // Topic-wise accuracy popup for a clicked chapter (the granular drill-down).
  renderTopicModal = () => {
    const cm = this.state.chapterModal;
    if (!cm) return null;
    const topics = (cm.topics || [])
      .slice()
      .sort((a, b) => num(a.accuracy_percentage) - num(b.accuracy_percentage));
    return (
      <Modal show={!!cm} onHide={this.closeChapterTopics} centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f2f5" }}>
          <Modal.Title
            style={{
              fontSize: 16,
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
            }}
          >
            {cm.subject} · {cm.chapter}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-baseline mb-3" style={{ gap: 8 }}>
            <span
              style={{
                fontSize: "1.8rem",
                fontWeight: 700,
                fontFamily: "'Outfit', sans-serif",
                color: this.accuracyColor(cm.accuracy),
                lineHeight: 1,
              }}
            >
              {cm.accuracy}%
            </span>
            <span className="text-muted" style={{ fontSize: 13 }}>
              chapter accuracy · {topics.length} topics (weakest first)
            </span>
          </div>
          {topics.map((t) => {
            const pct = num(t.accuracy_percentage);
            const color = this.accuracyColor(pct);
            return (
              <div key={t.topic_id ?? t.topic} className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-dark" style={{ fontSize: 13.5 }}>
                    {t.topic || "-"}
                  </span>
                  <span
                    className="font-weight-bold"
                    style={{ fontSize: 13.5, color }}
                  >
                    {pct}%
                  </span>
                </div>
                <div
                  style={{
                    height: 7,
                    borderRadius: 6,
                    background: "#eef2f6",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(Math.max(pct, 0), 100)}%`,
                      height: "100%",
                      borderRadius: 6,
                      background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`,
                    }}
                  />
                </div>
                {t.attempted != null ? (
                  <span className="text-muted" style={{ fontSize: 10.5 }}>
                    {t.attempted} attempted
                  </span>
                ) : null}
              </div>
            );
          })}
        </Modal.Body>
      </Modal>
    );
  };

  // Selected exam's per-subject breakdown (from examSubjectMatrix): chart + detail cards.
  // Aggregate every exam's per-subject rows into one cumulative "exam" object:
  // attempted & score are summed; accuracy is the attempt-weighted mean.
  aggregateSubjectMatrix = (matrix) => {
    const bySubject = new Map();
    matrix.forEach((exam) => {
      (exam.subjects || []).forEach((s) => {
        const key = s.subject_id ?? s.subject;
        if (!bySubject.has(key)) {
          bySubject.set(key, { subject_id: s.subject_id, subject: s.subject, attempted: 0, score: 0, subject_max: 0, accSum: 0, accWt: 0 });
        }
        const agg = bySubject.get(key);
        const att = num(s.attempted);
        agg.attempted += att;
        agg.score += num(s.score);
        agg.subject_max += num(s.subject_max); // cumulative max = sum of per-exam maxes
        // Weight accuracy by attempted so exams with more questions count more.
        agg.accSum += num(s.accuracy) * (att || 1);
        agg.accWt += att || 1;
      });
    });
    const subjects = [...bySubject.values()].map((a) => ({
      subject_id: a.subject_id,
      subject: a.subject,
      attempted: a.attempted,
      score: Math.round(a.score),
      subject_max: a.subject_max,
      accuracy: a.accWt ? Math.round((a.accSum / a.accWt) * 100) / 100 : 0,
    }));
    return { exam_name: "All exams", date: "", subjects, _cumulative: true };
  };

  renderExamSubjectMatrix = (matrix, selectedPaper) => {
    // "All exams" (value 0) → cumulative aggregate; otherwise the picked exam.
    const isCumulative = selectedPaper && String(selectedPaper.value) === "0";
    const selected = isCumulative
      ? (matrix.length ? this.aggregateSubjectMatrix(matrix) : null)
      : (matrix.find(
          (m) => String(m.exam_paper_id) === String(selectedPaper?.value),
        ) || (matrix.length ? matrix[matrix.length - 1] : null));

    if (!selected) {
      return (
        <div
          className="p-3"
          style={{
            borderRadius: "16px",
            border: "1px solid #f1f2f5",
            background: "#ffffff",
          }}
        >
          <div
            className="text-center text-muted py-4"
            style={{ fontSize: "14px" }}
          >
            No exam-wise subject data available.
          </div>
        </div>
      );
    }

    const subjects = selected.subjects || [];
    const categories = subjects.map((s) => s.subject);
    // Dynamic marks axis max = the largest per-subject max marks among the shown
    // subjects (e.g. 100). Score & Attempted sit on this axis; Accuracy % gets its
    // own fixed 0–100 axis. Fully derived, never hardcoded.
    const subjectMax = subjects.reduce((mx, s) => Math.max(mx, num(s.subject_max)), 0);
    const marksAxisMax = subjectMax > 0 ? subjectMax : null;
    const options = {
      chart: { type: "column", backgroundColor: "transparent", height: 320 },
      title: { text: "" },
      credits: { enabled: false },
      xAxis: {
        categories,
        lineColor: "#e2e8f0",
        labels: { style: { color: "#64748b", fontWeight: "600" } },
      },
      yAxis: [
        {
          // Axis 0 — marks / question count (Score, Attempted). Stops EXACTLY at
          // the max (e.g. 300) AND shows a label there (endOnTick alone hides it).
          min: 0,
          max: marksAxisMax,
          endOnTick: false,
          startOnTick: true,
          maxPadding: 0,
          showLastLabel: true,
          tickPositioner: marksAxisMax
            ? function () {
                const step = Math.ceil(marksAxisMax / 4);
                const ticks = [];
                for (let v = 0; v < marksAxisMax; v += step) ticks.push(v);
                ticks.push(marksAxisMax);
                return ticks;
              }
            : undefined,
          title: { text: marksAxisMax ? `Marks (out of ${marksAxisMax})` : "Marks", style: { color: "#94a3b8", fontSize: "11px" } },
          gridLineColor: "#eef2f6",
          labels: { style: { color: "#64748b" } },
        },
        {
          // Axis 1 — accuracy %, always 0–100.
          min: 0,
          max: 100,
          endOnTick: false,
          maxPadding: 0,
          opposite: true,
          title: { text: "Accuracy %", style: { color: "#22c55e", fontSize: "11px" } },
          gridLineWidth: 0,
          labels: { format: "{value}%", style: { color: "#22c55e" } },
        },
      ],
      tooltip: pointTooltip({ unitBySeries: { "Accuracy %": "%" } }),
      legend: { align: "center", verticalAlign: "top" },
      plotOptions: {
        column: {
          borderRadius: 4,
          groupPadding: 0.12,
          pointPadding: 0.04,
          dataLabels: {
            enabled: true,
            style: {
              fontWeight: "700",
              fontSize: "11px",
              color: "#475569",
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Attempted",
          yAxis: 0,
          data: subjects.map((s) => num(s.attempted)),
          color: "#6366f1",
        },
        {
          name: "Score",
          yAxis: 0,
          data: subjects.map((s) => num(s.score)),
          color: "#f59e0b",
        },
        {
          name: "Accuracy %",
          type: "spline",
          yAxis: 1,
          data: subjects.map((s) => num(s.accuracy)),
          color: "#22c55e",
          tooltip: { valueSuffix: "%" },
        },
      ],
    };

    return (
      <div
        className="p-3"
        style={{
          borderRadius: "16px",
          border: "1px solid #f1f2f5",
          background: "#ffffff",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between flex-wrap mb-3"
          style={{ gap: 8 }}
        >
          <h6
            className="font-weight-bold text-dark mb-0"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <i
              className="fas fa-chart-column mr-2"
              style={{ color: "#7c3aed" }}
            />
            {selected._cumulative ? "All Exams (cumulative)" : (selected.exam_name || "Exam")} — Subject Performance
          </h6>
          {selected._cumulative ? (
            <span className="text-muted" style={{ fontSize: 12 }}>
              <i className="fas fa-layer-group mr-1" />
              {matrix.length} exams combined
            </span>
          ) : null}
        </div>

        {subjects.length > 0 ? (
          <>
            {this.state.panelView === "table" ? (
              <div className="table-responsive">
                <table className="table table-borderless mb-0">
                  <thead>
                    <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                      <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Subject</th>
                      <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Attempted</th>
                      <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Score</th>
                      <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((s) => (
                      <tr key={s.subject_id ?? s.subject} style={{ borderBottom: "1px solid #f6f8fb" }}>
                        <td className="font-weight-bold text-dark" style={{ fontSize: 13 }}>{s.subject || "-"}</td>
                        <td className="text-right" style={{ fontSize: 13 }}>{num(s.attempted)}</td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>
                          {num(s.score)}{num(s.subject_max) > 0 ? <span className="text-muted" style={{ fontSize: 11 }}> / {num(s.subject_max)}</span> : null}
                        </td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13, color: this.accuracyColor(num(s.accuracy)) }}>{num(s.accuracy)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { width: "100%" } }}
              />
            )}
            <Row className="mt-3">
              {subjects.map((s) => (
                <Col
                  md={4}
                  sm={6}
                  key={s.subject_id ?? s.subject}
                  className="mb-3"
                >
                  <div
                    className="p-3 h-100"
                    style={{
                      borderRadius: 14,
                      border: "1px solid #f1f2f5",
                      background: "#fafaff",
                    }}
                  >
                    <div
                      className="font-weight-bold text-dark mb-2"
                      style={{ fontSize: 14 }}
                    >
                      {s.subject || "-"}
                    </div>
                    <div
                      className="d-flex justify-content-between text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      <span>Attempted</span>
                      <span className="font-weight-bold text-dark">
                        {num(s.attempted)}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      <span>Accuracy</span>
                      <span
                        className="font-weight-bold"
                        style={{ color: this.accuracyColor(num(s.accuracy)) }}
                      >
                        {num(s.accuracy)}%
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      <span>Score</span>
                      <span className="font-weight-bold text-dark">
                        {num(s.score)}
                      </span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div
            className="text-center text-muted py-4"
            style={{ fontSize: "14px" }}
          >
            No subject data for this exam.
          </div>
        )}
      </div>
    );
  };

  render() {
    const {
      subjectPerformance,
      chapterPerformance,
      accuracyAnalysis,
      examSubjectMatrix,
      selectedExamPaper,
      loading,
    } = this.state;

    return (
      <TabSection
        icon="fa-eye"
        title="Overview"
        description="Consolidated snapshot of the student's exam performance."
        headerRight={this.renderControls()}
      >
        {loading ? (
          <PageShimmer />
        ) : (
          <Row>
            {/* One shared Graph/Table toggle for both top panels. */}
            <Col sm={12} className="mb-2 d-flex justify-content-end">
              <div className="sp-segment sp-segment-sm">
                <button type="button" className={this.state.panelView === "graph" ? "active" : ""} onClick={() => this.setState({ panelView: "graph" })}>
                  <i className="fas fa-chart-column" />Graph
                </button>
                <button type="button" className={this.state.panelView === "table" ? "active" : ""} onClick={() => this.setState({ panelView: "table" })}>
                  <i className="fas fa-table" />Table
                </button>
              </div>
            </Col>
            <Col md={6} style={{ minWidth: 0 }} className="mb-4">
              {this.renderExamSubjectMatrix(
                examSubjectMatrix,
                selectedExamPaper,
              )}
            </Col>
            <Col md={6} style={{ minWidth: 0 }} className="mb-4">
              {this.renderAccuracyAnalysis(accuracyAnalysis)}
            </Col>

            {/* Overall Subject Wise Performance — commented out per request.
                        <Col md={6} style={{ minWidth: 0 }} className="mt-4">
                            {this.renderSubjectWisePerformance(subjectPerformance)}
                        </Col>
                        */}

            <Col sm={12} style={{ minWidth: 0 }}>
              {this.renderChapterWiseAccuracy(chapterPerformance)}
            </Col>
          </Row>
        )}
      </TabSection>
    );
  }
}

export default compose(withApollo, withRouter)(SubjectsTopicsTab);
